import { baseURL } from "../api/axios";

const SESSION_KEY = "webprofit.site-preview.v1";
const PREVIEW_PARAM_NAMES = Object.freeze([
  "wpPreview",
  "previewToken",
  "previewEndpoint",
  "previewMethod",
  "previewRevision",
  "previewExpiresAt",
]);
const TOKEN_PATTERN = /^wp\.preview\.v1\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
const MAX_TOKEN_LENGTH = 4096;

function browserAvailable() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function clean(value) {
  return String(value || "").trim();
}

function safeSessionStorage() {
  if (!browserAvailable()) return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function absoluteApiBase() {
  if (!browserAvailable()) return null;
  try {
    return new URL(String(baseURL || "/api"), window.location.origin);
  } catch {
    return null;
  }
}

function expectedPreviewEndpoint() {
  const apiBase = absoluteApiBase();
  if (!apiBase) return "";

  try {
    const normalizedBase = `${apiBase.toString().replace(/\/+$/, "")}/`;
    return new URL("public/landing/preview", normalizedBase).toString();
  } catch {
    return "";
  }
}

function normalizePreviewEndpoint(candidate) {
  const expected = expectedPreviewEndpoint();
  if (!expected) return "";

  try {
    const expectedUrl = new URL(expected);
    const candidateUrl = new URL(clean(candidate) || expected, window.location.origin);
    const sameOrigin = candidateUrl.origin === expectedUrl.origin;
    const samePath =
      candidateUrl.pathname.replace(/\/+$/, "") ===
      expectedUrl.pathname.replace(/\/+$/, "");

    if (
      !sameOrigin ||
      !samePath ||
      candidateUrl.username ||
      candidateUrl.password ||
      !["http:", "https:"].includes(candidateUrl.protocol)
    ) {
      return "";
    }

    candidateUrl.search = "";
    candidateUrl.hash = "";
    return candidateUrl.toString();
  } catch {
    return "";
  }
}

function hashParts() {
  if (!browserAvailable()) {
    return { route: "/", params: new URLSearchParams() };
  }

  const raw = clean(window.location.hash).replace(/^#/, "");
  const queryIndex = raw.indexOf("?");
  const route = clean(queryIndex >= 0 ? raw.slice(0, queryIndex) : raw) || "/";
  const query = queryIndex >= 0 ? raw.slice(queryIndex + 1) : "";
  return { route, params: new URLSearchParams(query) };
}

function incomingPreviewParams() {
  if (!browserAvailable()) {
    return { requested: false, params: new URLSearchParams() };
  }

  const hash = hashParts();
  const search = new URLSearchParams(window.location.search || "");
  const params = new URLSearchParams(search);
  hash.params.forEach((value, key) => params.set(key, value));

  return {
    requested:
      params.get("wpPreview") === "1" ||
      params.has("previewToken") ||
      params.has("previewEndpoint"),
    params,
  };
}

function removePreviewParamsFromAddress() {
  if (!browserAvailable()) return;

  try {
    const url = new URL(window.location.href);
    PREVIEW_PARAM_NAMES.forEach((name) => url.searchParams.delete(name));

    const { route, params } = hashParts();
    PREVIEW_PARAM_NAMES.forEach((name) => params.delete(name));
    const remainingHashQuery = params.toString();
    url.hash = `#${route}${remainingHashQuery ? `?${remainingHashQuery}` : ""}`;

    window.history.replaceState(
      window.history.state,
      document.title,
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    // Si el navegador bloquea history.replaceState, la validación y la sesión
    // siguen funcionando; nunca se vuelve a copiar el token a otra ubicación.
  }
}

function normalizeExpiry(value) {
  const raw = clean(value);
  const timestamp = Date.parse(raw);
  if (!raw || !Number.isFinite(timestamp)) return null;
  return { iso: new Date(timestamp).toISOString(), timestamp };
}

function normalizeRevision(value) {
  const revision = Number(value);
  return Number.isSafeInteger(revision) && revision > 0 ? revision : 0;
}

function normalizeProjectKey(value) {
  return clean(value).toLowerCase().slice(0, 180);
}

function validateSession(candidate) {
  if (!candidate || typeof candidate !== "object") {
    return { session: null, error: "La vista previa no contiene una sesión válida." };
  }

  const token = clean(candidate.token);
  const endpoint = normalizePreviewEndpoint(candidate.endpoint);
  const expires = normalizeExpiry(candidate.expiresAt);
  const method = clean(candidate.method || "POST").toUpperCase();
  const projectKey = normalizeProjectKey(candidate.projectKey);

  if (!token || token.length > MAX_TOKEN_LENGTH || !TOKEN_PATTERN.test(token)) {
    return { session: null, error: "El token de vista previa no es válido." };
  }
  if (!endpoint) {
    return {
      session: null,
      error: "La vista previa apunta a un servidor distinto del configurado para este sitio.",
    };
  }
  if (method !== "POST") {
    return { session: null, error: "El método de la vista previa no es compatible." };
  }
  if (!projectKey) {
    return { session: null, error: "La vista previa no identifica el proyecto." };
  }
  if (!expires || expires.timestamp <= Date.now()) {
    return { session: null, error: "La vista previa temporal venció." };
  }

  return {
    session: {
      token,
      endpoint,
      method: "POST",
      projectKey,
      draftRevision: normalizeRevision(candidate.draftRevision),
      expiresAt: expires.iso,
      capturedAt: clean(candidate.capturedAt) || new Date().toISOString(),
    },
    error: "",
  };
}

function readStoredSession() {
  const storage = safeSessionStorage();
  if (!storage) return { session: null, error: "" };

  try {
    const raw = storage.getItem(SESSION_KEY);
    if (!raw) return { session: null, error: "" };

    const validated = validateSession(JSON.parse(raw));
    if (!validated.session) storage.removeItem(SESSION_KEY);
    return validated;
  } catch {
    try {
      storage.removeItem(SESSION_KEY);
    } catch {
      // Sin acción: el navegador puede bloquear el almacenamiento de sesión.
    }
    return { session: null, error: "La sesión de vista previa no pudo recuperarse." };
  }
}

function storeSession(session) {
  const storage = safeSessionStorage();
  if (!storage) {
    return "El navegador no permite conservar la vista previa durante esta pestaña.";
  }

  try {
    storage.setItem(SESSION_KEY, JSON.stringify(session));
    return "";
  } catch {
    return "El navegador no permite conservar la vista previa durante esta pestaña.";
  }
}

export function clearSitePreviewSession() {
  const storage = safeSessionStorage();
  if (!storage) return;
  try {
    storage.removeItem(SESSION_KEY);
  } catch {
    // No se propaga un error de almacenamiento al flujo público.
  }
}

/**
 * Captura el token que el panel coloca en el fragmento, lo retira de la barra
 * de direcciones y lo conserva únicamente en sessionStorage de esta pestaña.
 */
export function resolveSitePreviewSession() {
  const incoming = incomingPreviewParams();

  if (!incoming.requested) {
    const stored = readStoredSession();
    return {
      requested: Boolean(stored.session || stored.error),
      session: stored.session,
      error: stored.error,
    };
  }

  const params = incoming.params;
  const candidate = {
    token: params.get("previewToken"),
    endpoint: params.get("previewEndpoint"),
    method: params.get("previewMethod") || "POST",
    projectKey: params.get("project") || params.get("projectId"),
    draftRevision: params.get("previewRevision"),
    expiresAt: params.get("previewExpiresAt"),
    capturedAt: new Date().toISOString(),
  };

  removePreviewParamsFromAddress();
  clearSitePreviewSession();

  const validated = validateSession(candidate);
  if (!validated.session) {
    return { requested: true, session: null, error: validated.error };
  }

  const storageError = storeSession(validated.session);
  if (storageError) {
    return { requested: true, session: null, error: storageError };
  }

  return { requested: true, session: validated.session, error: "" };
}

export function shouldDiscardSitePreviewSession(error) {
  const status = Number(error?.response?.status || error?.status || 0);
  const code = clean(error?.response?.data?.code || error?.code).toUpperCase();
  return (
    [401, 403, 404, 409, 410].includes(status) ||
    code.startsWith("SITE_PREVIEW_TOKEN_") ||
    code === "SITE_PREVIEW_ACCESS_REVOKED" ||
    code === "SITE_PREVIEW_NOT_FOUND" ||
    code === "SITE_PREVIEW_DRAFT_NOT_FOUND"
  );
}
